import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DashboardFilter } from '@app-model/dashboard';
import { PromotedContentType, getPromotedContentTypeIcon } from '@app-model/promotedContent';
import { DashboardService } from '@app-services/dashboard.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
	PromotedContentOfferPreviewContext,
	PromotedContentListingDetails,
	PromotedContentSubItem
} from './promoted-content-offer-preview.models';

type PromotedContentLayoutVariant = 'single' | 'gallery' | 'media' | 'html' | 'default';

@Component({
	selector: 'app-promoted-content-offer-preview',
	templateUrl: './promoted-content-offer-preview.component.html',
	styleUrls: ['./promoted-content-offer-preview.component.scss']
})
export class PromotedContentOfferPreviewComponent implements OnChanges, OnDestroy {
	@Input() context: PromotedContentOfferPreviewContext | null = null;
	@Input() dashboardFilter: DashboardFilter | null = null;
	@Input() autoResolveDetails = true;
	@Input() expandable = false;
	@Input() expanded = false;

	readonly promotedContentType = PromotedContentType;
	private readonly galleryLayoutTypes = new Set<PromotedContentType>([
		PromotedContentType.REAL_ESTATE_RECENT_OFFERS,
		PromotedContentType.REAL_ESTATE_WIDGET,
		PromotedContentType.REAL_ESTATE_SEARCH_WIDGET,
		PromotedContentType.IMAGE_GRID,
		PromotedContentType.CARROUSEL,
		PromotedContentType.YOUTUBE_LIST
	]);
	private readonly singleMediaLayoutTypes = new Set<PromotedContentType>([
		PromotedContentType.BANNER,
		PromotedContentType.YOUTUBE_VIDEO,
		PromotedContentType.HOSTED_VIDEO
	]);

	loadingDetails = false;
	resolvedSubItems: PromotedContentSubItem[] | null = null;

	private destroy$ = new Subject<void>();
	private lastFetchedId: string | null = null;

	constructor(private readonly dashboardService: DashboardService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['context']) {
			this.resolvedSubItems = this.context?.prefetchedSubItems ?? this.context?.subItems ?? null;
			this.lastFetchedId = null;
		}

		if (changes['context'] || changes['dashboardFilter']) {
			this.maybeResolveDetails();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get resolvedType(): PromotedContentType | string | null {
		return this.context?.promotedContentType ?? null;
	}

	private get normalizedType(): PromotedContentType | null {
		return (this.context?.promotedContentType as PromotedContentType) ?? null;
	}

	get typeLabel(): string {
		return this.getPromotedContentTypeLabel(this.resolvedType);
	}

	get typeIconUrl(): string {
		const type = this.context?.promotedContentType as PromotedContentType | undefined;
		const iconFilename = type ? getPromotedContentTypeIcon(type) : null;
		return iconFilename ? `/assets/images/promoted-content/${iconFilename}` : '';
	}

	get primaryImageUrl(): string | null {
		const source = this.context?.imgUrl;
		if (!source) {
			return null;
		}
		if (Array.isArray(source)) {
			return source.find(Boolean) ?? null;
		}
		return source || null;
	}

	get realEstateListing(): PromotedContentListingDetails | null {
		// First check if listing exists directly on context (from main endpoint response)
		const directListing = (this.context as any)?.listing as PromotedContentListingDetails | undefined;
		if (directListing?.address || directListing?.price) {
			return directListing;
		}

		const listingFromContext = this.pickListing(this.context?.prefetchedSubItems ?? this.context?.subItems ?? null);
		if (listingFromContext) {
			return listingFromContext;
		}
		return this.pickListing(this.resolvedSubItems);
	}

	get hasListingData(): boolean {
		return !!this.realEstateListing;
	}

	get isRealEstateSingleOffer(): boolean {
		return this.context?.promotedContentType === PromotedContentType.REAL_ESTATE_SINGLE_OFFER;
	}

	get isGalleryLayout(): boolean {
		const type = this.normalizedType;
		return !!type && this.galleryLayoutTypes.has(type);
	}

	get isSingleMediaLayout(): boolean {
		const type = this.normalizedType;
		return !!type && this.singleMediaLayoutTypes.has(type);
	}

	get isHtmlLayout(): boolean {
		return this.normalizedType === PromotedContentType.HTML;
	}

	get galleryImages(): string[] {
		return Array.isArray(this.context?.imgUrl) ? this.context!.imgUrl.filter(Boolean) : [];
	}

	get galleryPreviewImages(): string[] {
		return this.galleryImages.slice(0, 3);
	}

	get galleryRemainderCount(): number {
		return Math.max(this.galleryImages.length - 3, 0);
	}

	get galleryIsExpanded(): boolean {
		return !!this.context?.showAllImages;
	}

	get singleMediaImage(): string | null {
		return this.primaryImageUrl;
	}

	get defaultTitle(): string {
		return this.context?.title || this.typeLabel;
	}

	get layoutVariant(): PromotedContentLayoutVariant {
		const type = this.normalizedType;
		if (type === PromotedContentType.REAL_ESTATE_SINGLE_OFFER) {
			return 'single';
		}
		if (type && this.galleryLayoutTypes.has(type)) {
			return 'gallery';
		}
		if (type && this.singleMediaLayoutTypes.has(type)) {
			return 'media';
		}
		if (type === PromotedContentType.HTML) {
			return 'html';
		}
		return 'default';
	}

	private maybeResolveDetails(): void {
		if (!this.autoResolveDetails || !this.context) {
			return;
		}

		if (!this.isRealEstateSingleOffer || this.hasListingData) {
			return;
		}

		if (!this.dashboardFilter || !this.context.id) {
			return;
		}

		if (this.loadingDetails || (this.lastFetchedId && this.lastFetchedId === this.context.id)) {
			return;
		}

		// Fallback: fetch sub-items only if listing data wasn't provided by the main endpoint
		this.fetchSubItems(this.context.id);
	}

	private fetchSubItems(promotedContentId: string): void {
		this.loadingDetails = true;
		this.dashboardService
			.getPromotedContentById(this.dashboardFilter as DashboardFilter, promotedContentId)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe({
				next: response => {
					this.lastFetchedId = promotedContentId;
					this.resolvedSubItems = this.normalizeSubItems(response);
					this.loadingDetails = false;
				},
				error: error => {
					console.error('Error fetching promoted content details', error);
					this.loadingDetails = false;
				}
			});
	}

	private pickListing(subItems: PromotedContentSubItem[] | null | undefined): PromotedContentListingDetails | null {
		if (!subItems?.length) {
			return null;
		}
		const itemWithListing = subItems.find(item => item?.listing);
		return itemWithListing?.listing ?? null;
	}

	private normalizeSubItems(payload: any): PromotedContentSubItem[] {
		if (!Array.isArray(payload)) {
			return [];
		}

		return payload.map((item: any) => ({
			url: item?.url ?? '',
			clicks: Number(item?.clicks ?? 0),
			visits: Number(item?.visits ?? 0),
			ctr: Number(item?.ctr ?? 0),
			imageUrl: item?.imageUrl ?? item?.listing?.imageUrl,
			listing: item?.listing
		}));
	}

	private getPromotedContentTypeLabel(type: PromotedContentType | string | null): string {
		if (!type) {
			return '';
		}

		const typeMap: Record<string, string> = {
			[PromotedContentType.CARROUSEL]: 'Carousel',
			[PromotedContentType.IMAGE_GRID]: 'Image Grid',
			[PromotedContentType.BANNER]: 'Banner',
			[PromotedContentType.REAL_ESTATE_SINGLE_OFFER]: 'Real Estate - Single Offer',
			[PromotedContentType.REAL_ESTATE_RECENT_OFFERS]: 'Real Estate - Recent Offers',
			[PromotedContentType.REAL_ESTATE_WIDGET]: 'Real Estate Widget',
			[PromotedContentType.HTML]: 'HTML',
			[PromotedContentType.YOUTUBE_VIDEO]: 'YouTube Video',
			[PromotedContentType.YOUTUBE_LIST]: 'YouTube List',
			[PromotedContentType.HOSTED_VIDEO]: 'Hosted Video',
			[PromotedContentType.REAL_ESTATE_SEARCH_WIDGET]: 'Real Estate Search Widget'
		};

		return typeMap[type] || type;
	}

	toggleShowAllImages(context: PromotedContentOfferPreviewContext, event: Event): void {
		event.stopPropagation();
		context.showAllImages = !context.showAllImages;
	}
}
